package com.milta.feature.aicredit.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.aicredit.domain.entity.AiCredit
import com.milta.feature.aicredit.domain.entity.AiCreditHistoryEntry
import com.milta.feature.aicredit.domain.usecase.GetMyAiCreditHistoryUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch

data class MyAiCreditState(
    val credit: AiCredit? = null,
    val isLoadingCredit: Boolean = true,
    /** "server_error" | "offline" — only shown while no credit is known. */
    val creditError: String? = null,
    val history: AiCreditHistoryState<AiCreditHistoryEntry> = AiCreditHistoryState(),
)

sealed interface MyAiCreditIntent {
    /** Opening the tab / "Réessayer": re-reads the credit and restarts the history at page 1. */
    data object Load : MyAiCreditIntent
    /** The list reached its bottom — loads the next page if any (R13). */
    data object LoadMoreHistory : MyAiCreditIntent
}

/**
 * MIL-98 — "Mon usage", tab "Crédit IA": the caller's own credit as a percentage, its reset date
 * and its paged history. Shared by the mobile Profile screen (M3) and the web "Paramètres" card (E2).
 */
class MyAiCreditViewModel(
    private val creditStore: AiCreditStore,
    private val getMyHistory: GetMyAiCreditHistoryUseCase,
) : MviViewModel<MyAiCreditState, MyAiCreditIntent>(MyAiCreditState(credit = creditStore.credit.value)) {

    init {
        creditStore.credit.onEach { credit -> setState { copy(credit = credit ?: this.credit) } }.launchIn(scope)
        onIntent(MyAiCreditIntent.Load)
    }

    override fun onIntent(intent: MyAiCreditIntent) {
        when (intent) {
            MyAiCreditIntent.Load -> load()
            MyAiCreditIntent.LoadMoreHistory -> loadMore()
        }
    }

    /** Bumped by [load]: a page answer from before a reset ("Réessayer") is ignored. */
    private var historyGeneration = 0
    private var historyJob: Job? = null

    private fun load() {
        historyGeneration++
        historyJob?.cancel()
        setState { copy(isLoadingCredit = true, creditError = null, history = AiCreditHistoryState()) }
        scope.launch {
            creditStore.refresh().fold(
                onSuccess = { setState { copy(isLoadingCredit = false) } },
                onFailure = { e ->
                    setState { copy(isLoadingCredit = false, creditError = if (e is ApiException) "server_error" else "offline") }
                },
            )
        }
        loadMore()
    }

    private fun loadMore() {
        val history = currentState.history
        if (history.isLoadingPage || !history.hasMore) return
        // Synchronous flag before launching: a second scroll event can't fetch the same page twice.
        setState { copy(history = history.copy(isLoadingPage = true, error = null)) }
        val generation = historyGeneration
        historyJob = scope.launch {
            val result = getMyHistory(history.nextPage, AI_CREDIT_HISTORY_PAGE_SIZE)
            if (generation != historyGeneration) return@launch
            result.fold(
                onSuccess = { page -> setState { copy(history = this.history.withPage(page) { it.id }) } },
                onFailure = { e -> setState { copy(history = this.history.withError(e)) } },
            )
        }
    }
}
